import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Tab, Tabs, TextField, Tooltip, Typography } from "@material-ui/core";
import ArrowBack from "@material-ui/icons/ArrowBack";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import CloudDoneOutlined from "@material-ui/icons/CloudDoneOutlined";
import CloudUploadOutlined from "@material-ui/icons/CloudUploadOutlined";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import ForbiddenPage from "../../components/ForbiddenPage";
import AgentKnowledgeConfiguration from "../../components/AgentKnowledgeConfiguration";
import AiCredentialsModal from "../../components/AiCredentialsModal";
import AgentPreviewPanel from "../../components/AgentPreviewPanel";
import { AuthContext } from "../../context/Auth/AuthContext";
import { agentEditorErrors } from "../../helpers/agentEditor";
import useAgentEditor from "./useAgentEditor";
import AgentSettings from "./AgentSettings";
import AgentInstructions from "./AgentInstructions";

const useStyles = makeStyles(theme => ({
  root: { width: "100%", minWidth: 0 },
  header: { position: "sticky", top: 0, zIndex: 10, background: theme.palette.background.paper, padding: theme.spacing(2), borderRadius: 14, marginBottom: theme.spacing(2) },
  heading: { display: "flex", alignItems: "center", gap: theme.spacing(1.5), flexWrap: "wrap" },
  name: { flex: "1 1 260px", minWidth: 0, [theme.breakpoints.down("xs")]: { flexBasis: 180 } },
  actions: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap", [theme.breakpoints.down("xs")]: { width: "100%", "& > button": { flex: 1 } } },
  metadata: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: theme.spacing(1), marginTop: theme.spacing(1), overflowWrap: "anywhere" },
  templateInfo: { display: "inline-flex", alignItems: "center", gap: theme.spacing(0.5), minWidth: 0 },
  saveStatus: {
    display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28,
    flexShrink: 0, borderRadius: "50%", cursor: "help", color: theme.palette.text.secondary,
    "&[data-state='pending']": { color: theme.palette.type === "dark" ? theme.palette.warning.light : theme.palette.warning.dark },
    "&[data-state='error']": { color: theme.palette.error.main },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  srOnly: { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap", border: 0 },
  paper: { borderRadius: 14, minWidth: 0, overflow: "hidden" },
  tab: { minWidth: 0, flex: 1, padding: theme.spacing(1), textTransform: "none" },
  panel: { padding: theme.spacing(3), [theme.breakpoints.down("xs")]: { padding: theme.spacing(2, 1.5) } },
  fieldset: { border: 0, padding: 0, margin: 0, minWidth: 0 },
  status: { minHeight: 240, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 },
  errorSummary: { marginBottom: theme.spacing(2), "& button": { textAlign: "left", justifyContent: "flex-start" } },
}));

export default function AgentEditor() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { promptId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const templateSlug = new URLSearchParams(location.search).get("template");
  const allowed = user?.profile !== "user";
  const editor = useAgentEditor({ promptId, templateSlug, allowed });
  const { form, origin, resources, savedPrompt, setField } = editor;
  const [tab, setTab] = useState(0);
  const [errors, setErrors] = useState([]);
  const [bindingsOpen, setBindingsOpen] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pendingExit, setPendingExit] = useState(null);
  const [credentialError, setCredentialError] = useState(false);

  const formRef = useRef(null);
  const dirtyRef = useRef(false);
  const allowNavigation = useRef(false);
  dirtyRef.current = editor.dirty || editor.saving;

  useEffect(() => {
    const unblock = history.block((next, action) => {
      if (!dirtyRef.current || allowNavigation.current) return undefined;
      setPendingExit({ next, action });
      return false;
    });
    const beforeUnload = event => {
      if (!dirtyRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => { unblock(); window.removeEventListener("beforeunload", beforeUnload); };
  }, [history]);

  const focusError = error => {
    setTab(error.tab);
    requestAnimationFrame(() => {
      const wrapper = Array.from(formRef.current?.querySelectorAll("[data-field]") || []).find(element => element.dataset.field === error.field);
      const control = wrapper?.querySelector('textarea, [contenteditable="true"], input:not([type="hidden"]):not([aria-hidden="true"]), [role="button"], button');
      (control || wrapper)?.focus();
      wrapper?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
  };
  const save = async event => {
    event.preventDefault();
    if (editor.saving) return;
    const nextErrors = agentEditorErrors({ form, origin, ...resources, currentModelId: savedPrompt?.model });
    setErrors(nextErrors);
    if (nextErrors.length) { focusError(nextErrors[0]); return; }
    const saved = await editor.save();
    if (!saved) return;
    toast.success("Agente salvo.");
    if (!promptId) {
      allowNavigation.current = true;
      history.replace(`/prompts/${saved.id}/edit`);
      allowNavigation.current = false;
    }
  };
  const bindings = savedPrompt?.bindings || {};
  const nameError = errors.find(error => error.field === "name");
  const title = promptId ? "Editar agente" : origin ? "Criar a partir de template" : "Criar agente";
  const saveStatus = editor.saving
    ? { state: "saving", label: "Salvando configuração…", icon: <CircularProgress size={18} color="inherit" aria-hidden="true" /> }
    : editor.saveError
      ? { state: "error", label: "Não foi possível salvar. Tente novamente.", icon: <ErrorOutline fontSize="small" /> }
      : editor.dirty || !savedPrompt
        ? { state: "pending", label: `${savedPrompt ? "Alterações não salvas" : "Agente ainda não salvo"}. Clique em Salvar agente.`, icon: <CloudUploadOutlined fontSize="small" /> }
        : { state: "saved", label: "Configuração salva", icon: <CloudDoneOutlined fontSize="small" /> };
  const updateField = (field, value) => {
    setField(field, value);
    setErrors(current => current.filter(error => error.field !== field && !(field === "values" && error.field.startsWith("variable.")) && !(["generatedPrompt", "composedValuesKey"].includes(field) && error.field === "composition")));
  };
  if (!allowed) return <MainContainer fullWidth><ForbiddenPage /></MainContainer>;
  if (editor.loading) return <MainContainer fullWidth><div className={classes.status} role="status"><CircularProgress /><Typography>Carregando editor de agente...</Typography></div></MainContainer>;
  if (editor.loadError) return <MainContainer fullWidth><div className={classes.status}><Alert severity="error">Não foi possível carregar o agente e suas configurações.</Alert><Button onClick={editor.retryLoad}>Tentar novamente</Button><Button onClick={() => history.push("/prompts")}>Voltar aos agentes</Button></div></MainContainer>;

  return <MainContainer fullWidth className={classes.root}>
    <form ref={formRef} onSubmit={save} noValidate>
      <Paper className={classes.header} variant="outlined">
        <div className={classes.heading}>
          <IconButton aria-label="Voltar aos agentes" onClick={() => history.push("/prompts")} disabled={editor.saving}><ArrowBack /></IconButton>
          <div className={classes.name} data-field="name"><Typography variant="caption" component="h1" color="textSecondary">{title}</Typography>
            <TextField id="agent-name" name="name" label="Nome do agente" fullWidth required value={form.name} onChange={event => updateField("name", event.target.value)} disabled={editor.saving} error={Boolean(nameError)} helperText={nameError?.message} inputProps={{ maxLength: 100 }} />
          </div>
          <div className={classes.actions}>
            <Button variant="outlined" onClick={() => setPreviewOpen(true)} disabled={!savedPrompt?.id || editor.dirty || editor.saving}>{savedPrompt ? "Testar agente" : "Salve para testar"}</Button>
            <Button type="submit" variant="contained" color="primary" disabled={editor.saving}>{editor.saving ? "Salvando..." : "Salvar agente"}</Button>
          </div>
        </div>
        <div className={classes.metadata}>
          <div className={classes.templateInfo}>
            <Typography variant="caption" color="textSecondary">{origin ? origin.name ? `Criado a partir de ${origin.name}` : "Criado a partir de um template" : "Agente criado do zero"}</Typography>
            {origin && <Tooltip title={`${origin.version ? `Revisão de origem: ${origin.version}. ` : ""}Editar este agente não altera o template público. Revisões futuras não são aplicadas automaticamente.${origin.latestVersion > origin.version ? ` Existe uma revisão ${origin.latestVersion} mais recente.` : ""}`} arrow interactive>
              <IconButton size="small" type="button" aria-label="Informações do template"><InfoOutlined fontSize="small" /></IconButton>
            </Tooltip>}
          </div>
          {bindings.total > 0 ? <Button size="small" onClick={() => setBindingsOpen(true)}>Usado em {bindings.total} locais</Button> : <Typography variant="caption" color="textSecondary">Sem vínculos de atendimento</Typography>}
          <Tooltip title={saveStatus.label} arrow interactive>
            <span className={classes.saveStatus} data-state={saveStatus.state} role="status" aria-label={saveStatus.label} aria-live="polite" aria-atomic="true" tabIndex={0}>
              {saveStatus.icon}<span className={classes.srOnly}>{saveStatus.label}</span>
            </span>
          </Tooltip>
        </div>
        {bindings.total > 0 && <Typography variant="caption" color="textSecondary">Ao salvar, os locais vinculados passam a usar esta configuração.</Typography>}
        {(!savedPrompt || editor.dirty) && <Typography variant="caption" component="p" color="textSecondary">{savedPrompt ? "Salve as alterações para testar" : "Preencha os campos obrigatórios e salve o agente para testar. Ao salvar, os campos pendentes serão indicados."}</Typography>}
      </Paper>
      {errors.length > 0 && <Alert severity="error" className={classes.errorSummary}><Typography>Revise os campos indicados antes de salvar.</Typography>{errors.map(error => <Box key={error.field}><Button size="small" color="inherit" onClick={() => focusError(error)}>{error.message}</Button></Box>)}</Alert>}
      {editor.saveError && <Alert severity="error" className={classes.errorSummary}>{editor.saveError}</Alert>}
      {credentialError && <Alert severity="error" onClose={() => setCredentialError(false)}>Não foi possível atualizar as credenciais. Abra Gerenciar credenciais para tentar novamente.</Alert>}
      <Paper variant="outlined" className={classes.paper}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} indicatorColor="primary" textColor="primary" variant="fullWidth" aria-label="Configuração do agente">
          {["Instruções", "Conhecimento", "Configurações"].map((label, index) => <Tab key={label} className={classes.tab} id={`agent-tab-${index}`} aria-controls={`agent-panel-${index}`} label={`${label}${errors.some(error => error.tab === index) ? " • Erro" : ""}`} />)}
        </Tabs>
        <fieldset className={classes.fieldset} disabled={editor.saving}>
          <section className={classes.panel} role="tabpanel" id="agent-panel-0" aria-labelledby="agent-tab-0" hidden={tab !== 0}>
            <AgentInstructions form={form} setField={updateField} origin={origin} companyName={resources.companyName} companyValues={resources.companyValues} errors={errors} disabled={editor.saving} />
          </section>
          <section className={classes.panel} role="tabpanel" id="agent-panel-1" aria-labelledby="agent-tab-1" hidden={tab !== 1}>
            <div data-field="knowledge" tabIndex={-1}><AgentKnowledgeConfiguration value={form.knowledge} onChange={value => updateField("knowledge", value)} bases={resources.bases} onBasesLoaded={editor.rememberBases} queues={resources.queues} loading={editor.saving} templateRequirement={origin?.knowledgeRequirement || "optional"} /></div>
          </section>
          <section className={classes.panel} role="tabpanel" id="agent-panel-2" aria-labelledby="agent-tab-2" hidden={tab !== 2}>
            <AgentSettings form={form} setField={updateField} origin={origin} resources={resources} savedPrompt={savedPrompt} errors={errors} configureCredentials={setCredentials} disabled={editor.saving} />
          </section>
        </fieldset>
      </Paper>
    </form>
    <Dialog open={bindingsOpen} onClose={() => setBindingsOpen(false)} fullWidth maxWidth="sm" aria-labelledby="agent-bindings-title"><DialogTitle id="agent-bindings-title">Locais que usam este agente</DialogTitle><DialogContent dividers>
      {[ ["connections", "Conexão"], ["queues", "Fila"], ["flows", "Fluxo"], ["activeTickets", "Atendimento"] ].map(([key, label]) => (bindings[key] || []).map(item => <Typography key={`${key}-${item.id}`} paragraph>{label}: {item.name || `#${item.id}`}</Typography>))}
    </DialogContent><DialogActions><Button onClick={() => setBindingsOpen(false)}>Fechar</Button></DialogActions></Dialog>
    <Dialog open={Boolean(pendingExit)} onClose={() => setPendingExit(null)} aria-labelledby="agent-exit-title"><DialogTitle id="agent-exit-title">Descartar alterações?</DialogTitle><DialogContent>Você tem alterações não salvas neste agente.</DialogContent><DialogActions><Button autoFocus onClick={() => setPendingExit(null)}>Continuar editando</Button><Button variant="contained" color="primary" disabled={editor.saving} onClick={() => {
      allowNavigation.current = true;
      const target = pendingExit;
      setPendingExit(null);
      try {
        history[target.action === "REPLACE" ? "replace" : "push"](target.next);
      } finally {
        allowNavigation.current = false;
      }
    }}>Descartar e sair</Button></DialogActions></Dialog>
    <AiCredentialsModal open={Boolean(credentials)} initialPurpose={credentials?.purpose} initialProvider={credentials?.provider} onSaved={() => editor.refreshCredentials().catch(() => setCredentialError(true))} onClose={() => { setCredentials(null); editor.refreshCredentials().catch(() => setCredentialError(true)); }} />
    <AgentPreviewPanel open={previewOpen} prompt={savedPrompt} blocked={editor.dirty || editor.saving} onClose={() => setPreviewOpen(false)} />
  </MainContainer>;
}
